import * as React from "react";
import { Dispatch } from "redux";
import { connect } from "react-redux";
import GanttChartContent from "./GanttChartContent";
import * as t2 from "@/lib/backend/reports.generated.types";
import * as BusinessLogic from "@/lib/businessLogic";
import { GridLines, GridGroups, GridTextDates, CurrentShiftDashedLine, type ZoomToPreset } from "./GanttChartGrid";
import { ReduxFilters } from "../reducers/logic/businessLogic";
import {
    UPDATE_PROPERTIES_ACTION_NAME,
    UPDATE_PLANNING_TABLE_HEIGHT,
    GanttChartSource,
    countRowsOnGantt,
    countUnscheduledLinesOnGantt,
    numStockRequirementsAfterResolution,
    getPlanningDetailsWidth,
    zoomGanttChart,
    zoomGanttChartReset,
} from "../reducers/gantt/properties";
import { classNames } from "@/lib/util";
import { ScrollHandler } from "./scrollHandler";
import SelectableSvg, { type Selection } from "../../SelectableSvg";
import {
    applyMultiselectFromSelection,
    applyGanttZoomFromSelection,
    GANTT_ORDER_RECT_TARGET_SELECTOR,
} from "./ganttRectangleSelection";
import { ZoomMenu } from "../toolbar/zoomMenu";

import type {
    UpdatePropertiesActionType,
    GanttChartSourceTypes,
    UpdatePlanningTableWidthAction,
    UpdatePlanningTableHeightAction,
} from "../reducers/gantt/properties";
import type { ToggleMultiselectOrdersAction, ClearMultiselectOrdersAction } from "../reducers/planningTable";
import type { ReportState } from "../reducers/report";
import type { ReduxState } from "../reducers/reducers";
import type { ResetGanttAction } from "../reducers/common";

const getReportCreatedAt = (report: ReportState) => {
    if (!report || !report.report_data) return null;
    return report.report_data.created_at;
};

type ActionTypes =
    | UpdatePropertiesActionType
    | ResetGanttAction
    | UpdatePlanningTableWidthAction
    | UpdatePlanningTableHeightAction
    | ToggleMultiselectOrdersAction
    | ClearMultiselectOrdersAction;

type OwnProps = {
    line_group_uuid: string;
    source_type: GanttChartSourceTypes;
    show_only_unscheduled?: boolean;
};

type Props = OwnProps & ReduxProps;

type State = {
    properties_loaded: boolean;
    scroll_counter: number;
};

class GanttChart extends React.Component<Props, State> {
    gantt_chart_ref = React.createRef<HTMLDivElement>();

    state: State = {
        properties_loaded: false,
        scroll_counter: 0,
    };

    phantomNode = React.createRef<SVGTextElement>();

    resizeListener: (() => void) | null = null;

    componentWillUnmount() {
        ReduxFilters.onUnmount();
        this.removeListeners();
    }

    componentDidMount = () => {
        // calculate width and height
        if (!this.resizeListener) {
            this.resizeListener = this.updateDimensions(this);
        }

        window.addEventListener("resize", this.resizeListener);

        if (this.props.report && this.props.report.report_data) {
            this.onUpdateReportUpdateProperties();
        }
    };

    componentDidUpdate = (prev_props: Props) => {
        if (
            prev_props.created_at !== this.props.created_at ||
            prev_props.unscheduled_orders !== this.props.unscheduled_orders
        ) {
            this.onUpdateReportUpdateProperties();
        }

        // figure out if valid lines changed, first guess is if the size changed
        let valid_lines_changed = false;
        if (prev_props.valid_lines_map == null && this.props.valid_lines_map != null) {
            valid_lines_changed = true;
        } else if (prev_props.valid_lines_map != null && this.props.valid_lines_map == null) {
            valid_lines_changed = true;
        } else if (prev_props.valid_lines_map != null && this.props.valid_lines_map != null) {
            valid_lines_changed = prev_props.valid_lines_map.size != this.props.valid_lines_map.size;
            // if the size did not change, we need to find at least one line that is different
            if (!valid_lines_changed) {
                for (const line of this.props.valid_lines_map.values()) {
                    if (!prev_props.valid_lines_map.has(line.line_uuid)) {
                        valid_lines_changed = true;
                        break;
                    }
                }
            }
        }

        if (valid_lines_changed) {
            const { count_non_empty_lines, num_unscheduled_lines } = this.props.properties;
            if (count_non_empty_lines !== null) {
                this.props.reduxDispatch({
                    type: UPDATE_PLANNING_TABLE_HEIGHT,
                    data: { count_non_empty_lines, num_unscheduled_lines },
                });
            }
        }

        if (this.gantt_chart_ref.current) {
            ScrollHandler.updateChartHorizontalScroll();
        }
    };

    removeListeners = () => {
        if (this.resizeListener) {
            window.removeEventListener("resize", this.resizeListener);
        }
    };

    updateDimensions = self => () => {
        const properties = self.props.properties;

        const count_non_empty_lines = properties.count_non_empty_lines;
        const num_unscheduled_lines = properties.num_unscheduled_lines;

        if (count_non_empty_lines && num_unscheduled_lines) {
            self.props.reduxDispatch({
                type: UPDATE_PLANNING_TABLE_HEIGHT,
                data: { count_non_empty_lines, num_unscheduled_lines },
            });
        }
    };

    getIsPropertiesLoaded = () => {
        return this.props.properties.loaded;
    };

    onUpdateReportUpdateProperties = () => {
        // when we recieve report data we must update a few properties:
        // - count_non_empty_lines height based on number of lines
        // - start day which simulation generates
        // - days because it is dependent on current shift, which changes over time
        const report_data = this.props.report.report_data;
        if (!report_data) return;

        const start_day = Math.floor(report_data.result.next_shift_time.shift_number / 3);
        let count_non_empty_lines = countRowsOnGantt({
            valid_lines_map: this.props.valid_lines_map || new Map(),
            line_group_uuid: this.props.line_group_uuid,
            report_data,
            source_type: this.props.source_type,
            count_orders: this.props.waterfall_mode,
        });

        const num_unscheduled_lines = countUnscheduledLinesOnGantt({
            valid_orders_map: this.props.valid_orders_map || new Map(),
            unscheduled_orders: this.props.unscheduled_orders,
            source_type: this.props.source_type,
        });

        const num_stock_requirements = numStockRequirementsAfterResolution(this.props.stock_requirements, {
            ...this.props.properties,
            current_shift: report_data.result.next_shift_time.shift_number,
        });

        const shifts = report_data.result.line_shift_features;
        if (!shifts[0]) {
            return;
        }
        const properties = {
            start_day,
            count_non_empty_lines,
            num_unscheduled_lines,
            loaded: true,
            source_type: this.props.source_type,
            num_stock_requirements,
            line_group_uuid: this.props.line_group_uuid,
            current_shift: report_data.result.next_shift_time.shift_number,
            show_only_unscheduled: this.props.show_only_unscheduled,
            num_shifts: shifts[0].shifts.length,
            waterfall_mode: this.props.waterfall_mode,
        };

        this.props.reduxDispatch({ type: UPDATE_PROPERTIES_ACTION_NAME, data: properties });
    };

    getSvgAttributes() {
        let svg_attributes = {};
        const { width, height } = this.props.properties;

        if (width || height) {
            svg_attributes = {
                width: width,
                height: height,
            };
        }

        return svg_attributes;
    }

    getSourceType = () => {
        return this.props.source_type || this.props.properties.source_type;
    };

    getZoomContext = () => {
        return {
            minDayWidth: this.props.properties.min_day_width,
            planningDetailsWidth: getPlanningDetailsWidth(this.props.properties.space_for_label),
        };
    };

    getZoomRange = () => {
        return ZoomMenu.getZoomRange(this.getZoomContext());
    };

    applyZoom = (dayWidth: number) => {
        const constrainedDayWidth = ZoomMenu.constrainDayWidth(dayWidth, this.getZoomRange());
        zoomGanttChart(constrainedDayWidth, this.getSourceType(), this.props.reduxDispatch);
    };

    onZoomToHeaderPreset = (preset: ZoomToPreset, target: Element) => {
        if (preset === "week") {
            this.applyZoom(ZoomMenu.zoomToWeek(this.getZoomContext()));
        } else if (preset === "day") {
            this.applyZoom(ZoomMenu.zoomToDay(this.getZoomContext()));
        } else {
            this.applyZoom(ZoomMenu.zoomToShift(this.getZoomContext()));
        }

        setTimeout(() => {
            ScrollHandler.scrollToElement(target);
        });
    };

    isMultiselectionEnabled(): boolean {
        return (
            this.props.is_ctrl_down &&
            this.getSourceType() === GanttChartSource.planning_table_parallel &&
            (BusinessLogic.getPlantTagValue("planning_table_multiple_orders_selection") as boolean)
        );
    }

    renderChart() {
        const space_for_borders = 3;
        // Allow the top border to be visible for the orders in 1. line
        const height = this.props.properties.top_padding - space_for_borders;
        const line_shifts = this.props.shifts[0] ? this.props.shifts[0].shifts : [];
        const shift_x_positions = this.props.shift_x_positions;
        return (
            <g id="grid">
                <text ref={this.phantomNode} fontSize={"14px"} fontFamily={"'Roboto', sans-serif"} fontWeight={"400"} />
                <rect height={height} width="100%" fill="#ffffff"></rect>
                <GridLines
                    gantt_chart_properties={this.props.properties}
                    shifts={line_shifts}
                    shift_x_positions={shift_x_positions}
                />
                <GridGroups
                    gantt_chart_properties={this.props.properties}
                    source_type={this.props.source_type}
                    shifts={line_shifts}
                    shift_x_positions={shift_x_positions}
                />
                <GanttChartContent source_type={this.props.source_type} />
                <g id="grid-groups-header-shifts" style={{ willChange: "transform" }}>
                    <rect width="100%" height={height} fill="white" />
                    <GridTextDates
                        gantt_chart_properties={this.props.properties}
                        source_type={this.props.source_type}
                        shifts={line_shifts}
                        shift_x_positions={shift_x_positions}
                        onZoomToPreset={this.onZoomToHeaderPreset}
                    />
                </g>
                {this.props.source_type !== GanttChartSource.planning_table_parallel && (
                    <CurrentShiftDashedLine
                        gantt_chart_properties={this.props.properties}
                        source_type={this.props.source_type}
                        shifts={line_shifts}
                        shift_x_positions={shift_x_positions}
                        report={this.props.report}
                    />
                )}
            </g>
        );
    }

    handleSelectableSvgOnSelect = (selection: Selection) => {
        const zoom_mode = !(this.props.is_ctrl_down && this.isMultiselectionEnabled());
        const source_type = this.getSourceType();
        const dispatch = this.props.reduxDispatch;

        if (zoom_mode) {
            applyGanttZoomFromSelection(selection, { source_type, dispatch });
        } else if (this.isMultiselectionEnabled()) {
            applyMultiselectFromSelection(selection, {
                lines_orders: this.props.lines_orders,
                multiselect_order_uuids: this.props.multiselect_order_uuids,
                source_type,
                dispatch,
            });
        }
    };

    handleOnDoubleClick = () => {
        zoomGanttChartReset(this.getSourceType(), this.props.reduxDispatch);
    };

    renderContent(): React.ReactNode {
        const zoomInteractionEnabled = !(this.props.is_ctrl_down && this.isMultiselectionEnabled());
        return (
            <g onDoubleClick={zoomInteractionEnabled ? this.handleOnDoubleClick : undefined}>
                <SelectableSvg
                    className={classNames({
                        "selectable-svg--zooming": zoomInteractionEnabled,
                    })}
                    onSelect={this.handleSelectableSvgOnSelect}
                    target_selector={GANTT_ORDER_RECT_TARGET_SELECTOR}
                >
                    {this.renderChart()}
                </SelectableSvg>
            </g>
        );
    }

    handleScroll = (event: React.UIEvent<HTMLElement>) => {
        ScrollHandler.setChartScrollLeft(event.currentTarget.scrollLeft);
    };

    render() {
        // Allow the top border to be visible for the orders in 1. line
        const svg_attributes = this.getSvgAttributes();
        const class_name = classNames(`UUID_${this.props.report.original_report_uuid ?? ""}`, "js-chart-scrollable", {
            "chart-wrapper": !this.props.properties.no_overflow,
        });
        return (
            <div
                ref={this.gantt_chart_ref}
                id={this.getSourceType()}
                style={{ zIndex: 1, opacity: 1 }}
                className={class_name}
                onScroll={this.handleScroll}
            >
                {getReportCreatedAt(this.props.report) && (
                    <svg id="gantt-chart" data-testid="gantt-chart" {...svg_attributes}>
                        {this.renderContent()}
                    </svg>
                )}
            </div>
        );
    }
}

const mapState = (state: ReduxState) => {
    const properties = state.gantt_chart_properties;
    const planning_table = state.gantt_chart_planning_table;
    const valid_lines_map = planning_table.show_valid_lines.valid_lines_map;
    const valid_orders_map = planning_table.show_valid_lines.valid_orders_map;
    const multiselect_order_uuids = planning_table.multiselect_order_uuids;
    const is_ctrl_down = planning_table.is_ctrl_down;
    const stock_requirements = state.stock_requirements.stock_requirements;
    const report = state.gantt_chart_report.report_data;
    const shift_x_positions = state.gantt_chart_properties.shift_x_positions;
    const waterfall_mode = state.gantt_chart_properties.waterfall_mode;
    const lines_orders = state.gantt_chart_lines_orders.lines_orders;
    const unscheduled_orders = state.gantt_chart_lines_orders.unscheduled_orders;

    let shifts: t2.ISimulationReportOrderLineShiftFeature[] = [];
    if (report) {
        shifts = report.result.line_shift_features;
    }
    return {
        report_loading: state.gantt_chart_report.report_loading,
        report: state.gantt_chart_report,
        created_at: getReportCreatedAt(state.gantt_chart_report),
        properties,
        unscheduled_orders,
        valid_lines_map,
        valid_orders_map,
        stock_requirements,
        shifts,
        shift_x_positions,
        waterfall_mode,
        multiselect_order_uuids,
        lines_orders,
        is_ctrl_down,
    };
};

const mapDispatch = (dispatch: Dispatch<ActionTypes>) => ({ reduxDispatch: dispatch });

type ReduxProps = ReturnType<typeof mapState> & ReturnType<typeof mapDispatch>;

export default connect<ReturnType<typeof mapState>, ReturnType<typeof mapDispatch>, OwnProps, ReduxState>(
    mapState,
    mapDispatch
)(GanttChart);
